/**
 * LandingNutritionLabelWidget — "Snap → DIL/NIS" (iter729, Feb 2026)
 *
 * A landing-page widget that lets an anonymous visitor upload photos of:
 *   • a NUTRITION FACTS LABEL from any packaged product, OR
 *   • a plated dish
 *
 * NEW in iter729:
 *   • History carousel of up to 20 scans/day (persists in localStorage)
 *   • "Combine all" button → aggregates all scanned items into one plate,
 *     calls `POST /api/public/meal/combine`, shows total DIL/NIS + CTA to
 *     open the full Plate Simulator (requires signup)
 *   • Footer hyperlink to the `nutramilo-nis` PyPI package (research proof)
 *
 * → Backend flow:
 *   • `POST /api/public/meal/analyze-photo` (Emergent LLM vision) →
 *     Returns {food_name, kcal, protein_g, carbs_g, fat_g, fiber_g,
 *              sugar_g?, saturated_fat_g?, sodium_mg?, portion_g,
 *              is_nutrition_label, confidence, dil_g, nis_percent, nis_tier}
 *   • `POST /api/public/meal/combine`  → deterministic aggregation
 *
 * Rate limit: 20/day for anonymous IP.
 * No auth required — the backend endpoints are public.
 * Colors + typography follow the same aesthetic as LandingCollagenType3Widget.
 * All strings in Bulgarian primary + English fallback via LanguageContext.
 */
import React, { useCallback, useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  Camera, Upload, ScanLine, Loader2, ArrowRight, X as XIcon,
  Sparkles, Info, AlertCircle, ExternalLink, Layers, Trash2, Share2, Download,
  Pencil, Check as CheckIcon,
} from "lucide-react";

// Uses the same backend URL as the rest of the frontend.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const MAX_FILE_MB = 4;
const MAX_HISTORY = 20;
const HISTORY_KEY_PREFIX = "nutramilo_scan_history_v1_";

// ─── LocalStorage helpers (per-day rolling history) ────────────────────
function todayKey() {
  return HISTORY_KEY_PREFIX + new Date().toISOString().slice(0, 10);
}
function loadHistory() {
  try {
    const raw = localStorage.getItem(todayKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_HISTORY) : [];
  } catch { return []; }
}
function saveHistory(list) {
  try {
    localStorage.setItem(todayKey(), JSON.stringify(list.slice(0, MAX_HISTORY)));
  } catch { /* quota — ignore */ }
}


// ─── Canvas PNG share generator (zero-dep, portrait 1080×1350 for IG/WA) ──
// Draws a branded shareable card with DIL + NIS badges, item summary,
// Nutramilo logo/URL. Returns a Blob (image/png).
function tierColorForCanvas(tier) {
  const t = (tier || "").toLowerCase();
  if (t === "low") return "#10b981";
  if (t === "medium" || t === "med") return "#f59e0b";
  if (t === "high") return "#ef4444";
  return "#64748b";
}
async function generateShareBlob(result, bg) {
  const W = 1080, H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // Gradient background
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, "#0f172a");
  grad.addColorStop(0.5, "#1e1b4b");
  grad.addColorStop(1, "#0c4a6e");
  ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);

  // Top accent bar
  const bar = ctx.createLinearGradient(0, 0, W, 0);
  bar.addColorStop(0, "#38bdf8");
  bar.addColorStop(0.5, "#a855f7");
  bar.addColorStop(1, "#ec4899");
  ctx.fillStyle = bar; ctx.fillRect(0, 0, W, 12);

  // Brand
  ctx.fillStyle = "#e2e8f0";
  ctx.font = "700 42px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.textBaseline = "top";
  ctx.fillText("Nutramilo", 60, 60);
  ctx.fillStyle = "#94a3b8";
  ctx.font = "500 22px system-ui, sans-serif";
  ctx.fillText(bg ? "AI Digital Health Twin" : "AI Digital Health Twin", 60, 115);

  // Section label
  ctx.fillStyle = "#c084fc";
  ctx.font = "800 24px system-ui, sans-serif";
  ctx.fillText(bg ? "КОМБИНИРАНО ЯСТИЕ" : "COMBINED PLATE", 60, 200);

  // Item summary (food_name) — auto-wrap to 2 lines
  const name = result.food_name || (bg ? "Ястие" : "Meal");
  ctx.fillStyle = "#ffffff";
  ctx.font = "900 56px system-ui, sans-serif";
  const maxWidth = W - 120;
  const words = name.split(" ");
  const lines = []; let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line); line = w;
    } else line = test;
    if (lines.length >= 2) break;
  }
  if (line && lines.length < 2) lines.push(line);
  if (lines.length === 2 && ctx.measureText(lines[1]).width > maxWidth) {
    while (ctx.measureText(lines[1] + "…").width > maxWidth) lines[1] = lines[1].slice(0, -1);
    lines[1] += "…";
  }
  lines.forEach((ln, i) => ctx.fillText(ln, 60, 240 + i * 64));

  // Item count
  if (result.item_count) {
    ctx.fillStyle = "#94a3b8";
    ctx.font = "500 22px system-ui, sans-serif";
    ctx.fillText(
      bg ? `${result.item_count} елемента комбинирани` : `${result.item_count} items combined`,
      60, 240 + lines.length * 64 + 10,
    );
  }

  // ─── DIL + NIS badges (side by side) ─────────────────────────────
  const badgeY = 500;
  const badgeH = 380;
  const gap = 40;
  const badgeW = (W - 120 - gap) / 2;

  const dilTier = result.dil_g <= 30 ? "low" : result.dil_g <= 60 ? "medium" : "high";
  const nisTier = (result.nis_tier || "").toLowerCase() || (result.nis_percent == null ? "n/a" : result.nis_percent < 30 ? "low" : result.nis_percent < 60 ? "medium" : "high");

  // DIL badge
  ctx.fillStyle = tierColorForCanvas(dilTier);
  roundRect(ctx, 60, badgeY, badgeW, badgeH, 32); ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = "700 26px system-ui, sans-serif";
  ctx.fillText("DIL", 60 + badgeW / 2 - ctx.measureText("DIL").width / 2, badgeY + 40);
  ctx.fillStyle = "#ffffff";
  const dilVal = (result.dil_g ?? 0).toFixed(1);
  ctx.font = "900 130px system-ui, sans-serif";
  const dvW = ctx.measureText(dilVal).width;
  ctx.fillText(dilVal, 60 + badgeW / 2 - dvW / 2 - 15, badgeY + 120);
  ctx.font = "700 40px system-ui, sans-serif";
  ctx.fillText("g", 60 + badgeW / 2 - dvW / 2 + dvW - 5, badgeY + 200);
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = "500 22px system-ui, sans-serif";
  const dilLbl = bg ? "инсулинов товар" : "insulin load";
  ctx.fillText(dilLbl, 60 + badgeW / 2 - ctx.measureText(dilLbl).width / 2, badgeY + badgeH - 60);

  // NIS badge
  const nisX = 60 + badgeW + gap;
  ctx.fillStyle = tierColorForCanvas(nisTier);
  roundRect(ctx, nisX, badgeY, badgeW, badgeH, 32); ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = "700 26px system-ui, sans-serif";
  ctx.fillText("NIS", nisX + badgeW / 2 - ctx.measureText("NIS").width / 2, badgeY + 40);
  ctx.fillStyle = "#ffffff";
  const nisVal = result.nis_percent == null ? "—" : Math.round(result.nis_percent).toString();
  ctx.font = "900 130px system-ui, sans-serif";
  const nvW = ctx.measureText(nisVal).width;
  ctx.fillText(nisVal, nisX + badgeW / 2 - nvW / 2 - 15, badgeY + 120);
  ctx.font = "700 40px system-ui, sans-serif";
  ctx.fillText("%", nisX + badgeW / 2 - nvW / 2 + nvW - 5, badgeY + 200);
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = "500 22px system-ui, sans-serif";
  const nisLbl = bg ? "инсулинов индекс" : "insulin score";
  ctx.fillText(nisLbl, nisX + badgeW / 2 - ctx.measureText(nisLbl).width / 2, badgeY + badgeH - 60);

  // Macros mini-row
  const macY = 940;
  ctx.fillStyle = "#94a3b8";
  ctx.font = "600 22px system-ui, sans-serif";
  const macTitle = bg ? "МАКРОСИ" : "MACROS";
  ctx.fillText(macTitle, 60, macY);
  ctx.fillStyle = "#e2e8f0";
  ctx.font = "700 32px system-ui, sans-serif";
  const macros = [
    { l: bg ? "калории" : "kcal",    v: `${Math.round(result.calories || 0)}` },
    { l: bg ? "белтъци" : "protein", v: `${(result.protein_g || 0).toFixed(0)}g` },
    { l: bg ? "въгл." : "carbs",     v: `${(result.carbs_g || 0).toFixed(0)}g` },
    { l: bg ? "мазн." : "fat",       v: `${(result.fat_g || 0).toFixed(0)}g` },
    { l: bg ? "фибри" : "fiber",     v: `${(result.fiber_g || 0).toFixed(0)}g` },
  ];
  const colW = (W - 120) / macros.length;
  macros.forEach((m, i) => {
    const x = 60 + i * colW + colW / 2;
    ctx.textAlign = "center";
    ctx.fillStyle = "#ffffff";
    ctx.font = "800 34px system-ui, sans-serif";
    ctx.fillText(m.v, x, macY + 40);
    ctx.fillStyle = "#94a3b8";
    ctx.font = "500 20px system-ui, sans-serif";
    ctx.fillText(m.l, x, macY + 82);
  });
  ctx.textAlign = "left";

  // Footer
  ctx.fillStyle = "rgba(255,255,255,0.08)";
  ctx.fillRect(0, H - 180, W, 180);
  ctx.fillStyle = "#38bdf8";
  ctx.font = "700 32px system-ui, sans-serif";
  ctx.fillText(bg ? "Заснемете своя етикет → nutramilo.com" : "Scan yours → nutramilo.com", 60, H - 140);
  ctx.fillStyle = "#94a3b8";
  ctx.font = "500 20px system-ui, sans-serif";
  ctx.fillText(
    bg ? "AI vision · DIL v2.0 · NIS v1.1.9 · open-source формули"
       : "AI vision · DIL v2.0 · NIS v1.1.9 · open-source formulas",
    60, H - 92,
  );
  ctx.fillStyle = "#64748b";
  ctx.font = "400 16px system-ui, sans-serif";
  ctx.fillText(
    bg ? "Образователна оценка · не заменя клинична консултация"
       : "Educational analysis · not a substitute for clinical advice",
    60, H - 56,
  );

  return await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));
}
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
}


export default function LandingNutritionLabelWidget() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const fileInputRef = useRef(null);

  const [preview, setPreview] = useState(null);        // data URL for local preview
  const [analyzing, setAnalyzing] = useState(false);
  const [combining, setCombining] = useState(false);
  const [result, setResult] = useState(null);          // latest single-scan result
  const [combinedResult, setCombinedResult] = useState(null); // aggregated meal
  const [sharing, setSharing] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [history, setHistory] = useState([]);          // up-to-20 scan items today
  const [quotaLeft, setQuotaLeft] = useState(null);
  const [error, setError] = useState(null);

  // Hydrate history from LS on mount
  useEffect(() => { setHistory(loadHistory()); }, []);

  const clearPreview = () => {
    setPreview(null); setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const clearHistory = () => {
    setHistory([]); setResult(null); setCombinedResult(null); clearPreview();
    try { localStorage.removeItem(todayKey()); } catch { /* noop */ }
  };

  // ─── File pick + upload flow ────────────────────────────────────────
  const onFilePicked = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(bg
        ? `Файлът е > ${MAX_FILE_MB} MB. Опитайте с по-малка снимка.`
        : `File is > ${MAX_FILE_MB} MB. Try a smaller photo.`);
      return;
    }
    setError(null);
    setCombinedResult(null);

    // Read as data URL for local preview
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);

    // Base64 for backend
    const b64 = await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || "").split(",", 2)[1]);
      r.readAsDataURL(file);
    });

    setAnalyzing(true);
    try {
      const resp = await axios.post(
        `${API}/public/meal/analyze-photo`,
        { image_base64: b64, language: bg ? "bg" : "en" },
        { timeout: 40000 },
      );
      const data = resp.data || {};
      if (data.error) throw new Error(data.error);
      const scanId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setResult({ ...data, id: scanId });
      if (typeof data.quota_remaining === "number") setQuotaLeft(data.quota_remaining);

      // Add to today's history (dedup by identical food_name+dil_g in last 2s)
      const item = {
        id: scanId,
        thumb: reader.result,   // data URL (small, kept in memory only)
        food_name: data.food_name,
        calories: data.calories,
        protein_g: data.protein_g,
        carbs_g: data.carbs_g,
        fat_g: data.fat_g,
        fiber_g: data.fiber_g,
        sugar_g: data.sugar_g,
        saturated_fat_g: data.saturated_fat_g,
        sodium_mg: data.sodium_mg,
        portion_g: data.portion_g,
        dil_g: data.dil_g,
        nis_percent: data.nis_percent,
        nis_tier: data.nis_tier,
        is_nutrition_label: data.is_nutrition_label,
        confidence: data.confidence,
        ts: Date.now(),
      };
      // Wait next tick so reader.result is filled
      setTimeout(() => {
        setHistory((h) => {
          const next = [item, ...h].slice(0, MAX_HISTORY);
          saveHistory(next);
          return next;
        });
      }, 50);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        const d = err.response.data?.detail || {};
        setQuotaLeft(0);
        setError({
          type: "quota",
          message: bg
            ? "Достигнахте дневния лимит от 20 снимки. Регистрирайте се безплатно за повече."
            : "Reached daily limit of 20 photos. Sign up free for more.",
          next_reset: d.next_reset_utc,
        });
      } else if (status === 413) {
        setError({ type: "size", message: bg ? "Файлът е твърде голям." : "File too large." });
      } else {
        setError({ type: "api", message: err?.response?.data?.detail || err.message || "Error" });
      }
    }
    setAnalyzing(false);
  }, [bg]);

  const triggerFilePick = () => fileInputRef.current?.click();
  const triggerCameraCapture = () => {
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute("capture", "environment");
      fileInputRef.current.click();
    }
  };

  const removeFromHistory = (id) => {
    setHistory((h) => {
      const next = h.filter((it) => it.id !== id);
      saveHistory(next);
      return next;
    });
    setCombinedResult(null);
  };

  const handleCombineAll = async () => {
    if (history.length < 2) {
      toast.info(bg
        ? "Нужни са поне 2 заснети етикета за обобщение."
        : "Need at least 2 scans to combine.");
      return;
    }
    setCombining(true);
    setCombinedResult(null);
    try {
      const items = history.map((h) => ({
        food_name: h.food_name,
        calories: h.calories,
        protein_g: h.protein_g,
        carbs_g: h.carbs_g,
        fat_g: h.fat_g,
        fiber_g: h.fiber_g,
        sugar_g: h.sugar_g,
        saturated_fat_g: h.saturated_fat_g,
        sodium_mg: h.sodium_mg,
        portion_g: h.portion_g,
      }));
      const resp = await axios.post(
        `${API}/public/meal/combine`,
        { items, language: bg ? "bg" : "en" },
        { timeout: 15000 },
      );
      setCombinedResult(resp.data);
      // Scroll into view
      setTimeout(() => {
        document.querySelector('[data-testid="nl-combined-card"]')
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    } catch (err) {
      toast.error(bg ? "Комбинирането се провали." : "Combine failed.");
      console.error("combine failed", err);
    }
    setCombining(false);
  };

  const handleShare = async () => {
    if (!combinedResult) return;
    setSharing(true);
    try {
      const blob = await generateShareBlob(combinedResult, bg);
      if (!blob) { toast.error(bg ? "Неуспешно генериране на изображение." : "Failed to generate image."); setSharing(false); return; }
      const file = new File([blob], "nutramilo-plate.png", { type: "image/png" });
      const shareText = bg
        ? `Моето комбинирано ястие: DIL ${combinedResult.dil_g}g · NIS ${combinedResult.nis_percent != null ? Math.round(combinedResult.nis_percent) + "%" : "—"} · чрез Nutramilo`
        : `My combined plate: DIL ${combinedResult.dil_g}g · NIS ${combinedResult.nis_percent != null ? Math.round(combinedResult.nis_percent) + "%" : "—"} · via Nutramilo`;
      const shareUrl = window.location.origin;

      // Try Web Share API with files (mobile — WhatsApp/Instagram/Messenger etc.)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], text: shareText, url: shareUrl });
          toast.success(bg ? "Споделено." : "Shared.");
          setSharing(false);
          return;
        } catch (err) {
          // User cancelled — silent; fall through to download only if real error
          if (err?.name !== "AbortError") console.error("share failed", err);
          else { setSharing(false); return; }
        }
      }
      // Fallback — download the PNG so user can share it manually
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "nutramilo-plate.png";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(bg
        ? "PNG изтеглен · сподели го в WhatsApp / Facebook"
        : "PNG downloaded · share it on WhatsApp / Facebook");
    } catch (err) {
      console.error("share error", err);
      toast.error(bg ? "Грешка при споделянето." : "Share failed.");
    }
    setSharing(false);
  };

  /**
   * Re-compute DIL + NIS after the user edits the macros of a scanned item.
   * The AI vision sometimes mis-reads a nutrition label (e.g. rotated or blurry),
   * so this lets visitors correct the numbers and get accurate scores without
   * a re-scan (no AI cost, deterministic math).
   *
   * scope:
   *   • "single"     → the currently displayed single-scan `result` (also
   *                    updates the same item in history so combining reflects it)
   *   • historyId    → a specific history-item id
   */
  const handleRecalculate = async (edited, itemId) => {
    setRecalculating(true);
    try {
      const resp = await axios.post(
        `${API}/public/meal/combine`,
        { items: [edited], language: bg ? "bg" : "en" },
        { timeout: 15000 },
      );
      const merged = {
        ...resp.data,
        // Keep the original food name (combine builds "N items" summary otherwise)
        food_name: edited.food_name || resp.data.food_name,
        is_nutrition_label: true,
        confidence: 1.0,
        id: itemId,
        educational_note: bg
          ? "Ръчно коригирано · DIL и NIS % преизчислени върху редактираните макроси."
          : "Manually corrected · DIL and NIS % recomputed on your edits.",
      };
      // Update the currently-displayed result card
      setResult(merged);
      // Update the matching history item (by id) so combining reflects the fix
      setHistory((h) => {
        const next = h.map((it) => it.id === itemId
          ? { ...it, ...edited, dil_g: merged.dil_g, nis_percent: merged.nis_percent, nis_tier: merged.nis_tier }
          : it);
        saveHistory(next); return next;
      });
      // Invalidate combined result — it needs a rebuild
      setCombinedResult(null);
      toast.success(bg ? "Преизчислено." : "Recalculated.");
    } catch (err) {
      console.error("recalc failed", err);
      toast.error(bg ? "Преизчислението се провали." : "Recalculate failed.");
    }
    setRecalculating(false);
  };

  // ─── Render ─────────────────────────────────────────────────────────
  return (
    <section
      data-testid="landing-nutrition-label-widget"
      className={`relative overflow-hidden py-16 px-4 sm:px-6 lg:px-8 ${
        isDark ? "bg-gradient-to-b from-slate-950 via-sky-950/30 to-slate-950" : "bg-gradient-to-b from-white via-sky-50/40 to-white"
      }`}
    >
      <div className={`absolute inset-0 pointer-events-none opacity-40 ${
        isDark ? "bg-[radial-gradient(circle_at_80%_10%,rgba(56,189,248,0.15),transparent_40%),radial-gradient(circle_at_20%_60%,rgba(168,85,247,0.12),transparent_50%)]" : "bg-[radial-gradient(circle_at_80%_10%,rgba(56,189,248,0.10),transparent_40%),radial-gradient(circle_at_20%_60%,rgba(168,85,247,0.08),transparent_50%)]"
      }`} />

      <div className="max-w-5xl mx-auto relative">
        {/* Section header */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
            isDark ? "bg-sky-500/15 text-sky-300 border border-sky-500/30" : "bg-sky-100 text-sky-700 border border-sky-300"
          }`}>
            <ScanLine className="w-3 h-3" />
            {bg ? "AI Vision · Nutrition Facts Scanner" : "AI Vision · Nutrition Facts Scanner"}
          </div>
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-black mt-3 tracking-tight ${
            isDark ? "text-white" : "text-slate-900"
          }`}>
            {bg ? "Снимай етикет — виж " : "Snap a label — see "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-purple-400 to-pink-400">
              DIL & NIS
            </span>
            {bg ? " моментално" : " instantly"}
          </h2>
          <p className={`mt-2 text-sm sm:text-base max-w-2xl mx-auto ${
            isDark ? "text-slate-300" : "text-slate-600"
          }`}>
            {bg
              ? "Снимка на етикет от пакет или на цяло ястие — AI извлича макросите и пресмята вашия DIL (диетичен инсулинов товар) + NIS % (Nutramilo Insulin Score) за секунди."
              : "Photo of a package nutrition label or a plated dish — AI extracts the macros and computes your DIL (Dietary Insulin Load) + NIS % (Nutramilo Insulin Score) in seconds."}
          </p>
          <p className={`mt-1 text-[11px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>
            {bg
              ? `До 20 заснети етикета на ден · Натиснете «Комбинирай всички» за пълен клиничен ефект`
              : `Up to ${MAX_HISTORY} scans/day · Press "Combine all" for the full clinical impact`}
          </p>
        </div>

        {/* Body */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left — Upload area */}
          <div className={`p-5 rounded-2xl border relative ${
            isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-white shadow-lg"
          }`}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onFilePicked}
              className="hidden"
              data-testid="nl-file-input"
            />

            {!preview && (
              <div className="text-center py-6">
                <ScanLine className={`w-10 h-10 mx-auto ${isDark ? "text-sky-400" : "text-sky-500"}`} />
                <p className={`mt-2 text-sm ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                  {bg ? "Изберете снимка от галерията или направете нова" : "Pick a photo or take a new one"}
                </p>
                <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center">
                  <Button
                    onClick={triggerCameraCapture}
                    data-testid="nl-camera-btn"
                    className="gradient-primary text-white font-bold"
                  >
                    <Camera className="w-4 h-4 mr-1.5" />
                    {bg ? "Заснемане" : "Take photo"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={triggerFilePick}
                    data-testid="nl-upload-btn"
                    className={isDark ? "border-white/20 text-slate-200 hover:bg-white/10" : ""}
                  >
                    <Upload className="w-4 h-4 mr-1.5" />
                    {bg ? "Качване" : "Upload"}
                  </Button>
                </div>
                <p className={`mt-3 text-[10px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                  {bg
                    ? `JPG / PNG до ${MAX_FILE_MB} MB · до ${MAX_HISTORY} безплатни заснемания днес`
                    : `JPG / PNG up to ${MAX_FILE_MB} MB · up to ${MAX_HISTORY} free scans today`}
                </p>
              </div>
            )}

            {preview && (
              <div className="relative">
                <img
                  src={preview}
                  alt="preview"
                  className="w-full h-48 object-cover rounded-lg"
                  data-testid="nl-preview-image"
                />
                <button
                  onClick={clearPreview}
                  disabled={analyzing}
                  data-testid="nl-clear"
                  className="absolute top-2 right-2 h-7 w-7 bg-black/60 text-white rounded-full flex items-center justify-center hover:bg-black/80 disabled:opacity-50"
                >
                  <XIcon className="w-3.5 h-3.5" />
                </button>
                {analyzing && (
                  <div className="absolute inset-0 rounded-lg bg-black/60 flex flex-col items-center justify-center gap-2">
                    <Loader2 className="w-6 h-6 animate-spin text-white" />
                    <p className="text-white text-xs font-semibold">
                      {bg ? "AI анализирам…" : "AI analysing…"}
                    </p>
                  </div>
                )}
              </div>
            )}

            {preview && !analyzing && (
              <div className="mt-3 text-center">
                <Button
                  onClick={() => { clearPreview(); triggerFilePick(); }}
                  variant="outline"
                  size="sm"
                  data-testid="nl-scan-more-btn"
                  className={isDark ? "border-white/20 text-slate-200 hover:bg-white/10" : ""}
                >
                  <Camera className="w-3.5 h-3.5 mr-1.5" />
                  {bg ? "Още продукт" : "Scan another"}
                </Button>
              </div>
            )}

            {error && (
              <div className={`mt-3 p-3 rounded-lg border text-xs ${
                error.type === "quota"
                  ? (isDark ? "bg-amber-500/15 text-amber-100 border-amber-500/40" : "bg-amber-50 text-amber-900 border-amber-300")
                  : (isDark ? "bg-red-500/15 text-red-100 border-red-500/40" : "bg-red-50 text-red-900 border-red-300")
              }`}>
                <div className="flex items-start gap-1.5">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold">{error.message}</div>
                    {error.type === "quota" && (
                      <Link to="/register" className="mt-1 inline-block text-xs underline font-semibold">
                        {bg ? "Регистрирайте се безплатно →" : "Sign up free →"}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right — Latest scan result */}
          <div className={`p-5 rounded-2xl border relative flex flex-col ${
            isDark ? "border-white/10 bg-gradient-to-br from-slate-900 to-sky-950/30" : "border-slate-200 bg-gradient-to-br from-sky-50 to-white shadow-lg"
          }`}>
            {!result ? (
              <div className={`flex-1 flex flex-col items-center justify-center py-8 ${
                isDark ? "text-slate-500" : "text-slate-400"
              }`}>
                <Sparkles className="w-8 h-8 mb-2" />
                <p className="text-xs italic text-center">
                  {bg
                    ? "Резултатите ще се появят тук след анализа"
                    : "Results will appear here after analysis"}
                </p>
              </div>
            ) : (
              <ResultCard
                result={result}
                bg={bg}
                isDark={isDark}
                editable
                recalculating={recalculating}
                onRecalculate={(edited) => handleRecalculate(edited, result.id)}
              />
            )}
          </div>
        </div>

        {/* History carousel — up to 20 scans/day */}
        {history.length > 0 && (
          <div className="mt-8" data-testid="nl-history-carousel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Layers className={`w-4 h-4 ${isDark ? "text-sky-300" : "text-sky-600"}`} />
                <span className={`text-xs font-bold uppercase tracking-wider ${
                  isDark ? "text-sky-300" : "text-sky-700"
                }`}>
                  {bg ? `Заснети днес · ${history.length}/${MAX_HISTORY}` : `Today's scans · ${history.length}/${MAX_HISTORY}`}
                </span>
              </div>
              <button
                onClick={clearHistory}
                data-testid="nl-clear-history"
                className={`text-[11px] flex items-center gap-1 hover:underline ${isDark ? "text-slate-400" : "text-slate-500"}`}
              >
                <Trash2 className="w-3 h-3" />
                {bg ? "Изчисти" : "Clear"}
              </button>
            </div>

            <div className="flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-sky-400/40">
              {history.map((it) => (
                <HistoryCard
                  key={it.id}
                  item={it}
                  isDark={isDark}
                  bg={bg}
                  onRemove={() => removeFromHistory(it.id)}
                  onSelect={(item) => {
                    // Load the history item back into the right-hand result panel
                    // so the user can edit + recalculate its macros.
                    setResult({
                      ...item,
                      // preserve disclaimer keys that ResultCard reads
                      nutramilo_disclaimer_bg: result?.nutramilo_disclaimer_bg || item.nutramilo_disclaimer_bg,
                      nutramilo_disclaimer_en: result?.nutramilo_disclaimer_en || item.nutramilo_disclaimer_en,
                    });
                    setCombinedResult(null);
                    setTimeout(() => {
                      document.querySelector('[data-testid="nl-result-name"]')
                        ?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }, 50);
                  }}
                />
              ))}
            </div>

            {history.length >= 2 && (
              <div className="mt-4 flex justify-center">
                <Button
                  onClick={handleCombineAll}
                  disabled={combining}
                  data-testid="nl-combine-btn"
                  className="gradient-primary text-white font-bold shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50"
                >
                  {combining ? (
                    <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />{bg ? "Комбинирам…" : "Combining…"}</>
                  ) : (
                    <><Layers className="w-4 h-4 mr-1.5" />
                      {bg ? `Комбинирай всички ${history.length} елемента` : `Combine all ${history.length} items`}
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Combined result card (inline on landing) */}
        {combinedResult && (
          <div
            className={`mt-6 p-6 rounded-2xl border-2 border-purple-400 relative overflow-hidden ${
              isDark ? "bg-gradient-to-br from-slate-900 via-purple-950/30 to-sky-950/30" : "bg-gradient-to-br from-purple-50 via-white to-sky-50 shadow-2xl"
            }`}
            data-testid="nl-combined-card"
          >
            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-sky-400 via-purple-400 to-pink-400`} />
            <div className="flex items-center gap-2 mb-3">
              <Layers className={`w-5 h-5 ${isDark ? "text-purple-300" : "text-purple-600"}`} />
              <span className={`text-[10px] font-bold uppercase tracking-widest ${
                isDark ? "text-purple-300" : "text-purple-700"
              }`}>
                {bg ? "Комбинирано ястие · сумарен клиничен ефект" : "Combined plate · aggregate clinical impact"}
              </span>
            </div>
            <ResultCard result={combinedResult} bg={bg} isDark={isDark} isCombined />

            {/* COL3 Synthesis Preview — iter732 */}
            <Col3PreviewCard result={combinedResult} bg={bg} isDark={isDark} />

            {/* Share + CTA row */}
            <div className={`mt-5 pt-5 border-t ${isDark ? "border-white/10" : "border-slate-200"}`}>
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className={`text-xs font-bold uppercase tracking-wider ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                    {bg ? "Продължи с пълния Симулатор" : "Continue in the full Plate Simulator"}
                  </div>
                  <div className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                    {bg
                      ? "Свободна регистрация — микронутриенти, COL3 %, коен-инсулин, и цялостен NIS профил"
                      : "Free signup — micronutrients, COL3 %, insulin cohort, and full NIS breakdown"}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <Button
                    onClick={handleShare}
                    disabled={sharing}
                    variant="outline"
                    data-testid="nl-share-btn"
                    className={isDark ? "border-white/20 text-slate-100 hover:bg-white/10" : "border-slate-300 hover:bg-slate-100"}
                  >
                    {sharing ? (
                      <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />{bg ? "Генерирам…" : "Generating…"}</>
                    ) : (
                      <>
                        {typeof navigator !== "undefined" && navigator.canShare
                          ? <Share2 className="w-4 h-4 mr-1.5" />
                          : <Download className="w-4 h-4 mr-1.5" />}
                        {bg ? "Сподели резултата" : "Share result"}
                      </>
                    )}
                  </Button>
                  <Link to="/register" data-testid="nl-cta-register">
                    <Button className="gradient-primary text-white font-bold shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50">
                      <Sparkles className="w-4 h-4 mr-1.5" />
                      {bg ? "Регистрирай се безплатно" : "Sign up free"}
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Single-scan CTA (only when no combined result yet) */}
        {result && !combinedResult && (
          <div
            className={`mt-6 p-6 rounded-2xl border-2 border-emerald-400 bg-gradient-to-r from-emerald-500/10 via-sky-500/10 to-purple-500/10 flex flex-col md:flex-row items-center justify-between gap-4`}
            data-testid="nl-cta-card"
          >
            <div>
              <div className={`text-xs font-bold uppercase tracking-wider ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                {bg ? "Продължи анализа" : "Deepen the analysis"}
              </div>
              <div className={`text-base font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                {bg
                  ? `Заснемете още продукти → натисни «Комбинирай всички» → пълен клиничен ефект`
                  : `Scan more products → press "Combine all" → full clinical impact`}
              </div>
              {quotaLeft !== null && (
                <div className={`mt-1 text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  {bg
                    ? `Остават ${quotaLeft} безплатни заснемания днес`
                    : `${quotaLeft} free scans left today`}
                </div>
              )}
            </div>
            <Link to="/register" data-testid="nl-cta-register-single">
              <Button className="gradient-primary text-white font-bold shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50">
                <Sparkles className="w-4 h-4 mr-1.5" />
                {bg ? "Регистрирай се" : "Sign up free"}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        )}

        {/* Research-transparency footer — PyPI link */}
        <div className={`mt-10 pt-6 border-t text-center ${isDark ? "border-white/10" : "border-slate-200"}`}>
          <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {bg
              ? "Формулите DIL v2.0 и NIS v1.1.9 са open-source и рецензируеми. Пълен код, датасети и валидация в:"
              : "The DIL v2.0 and NIS v1.1.9 formulas are open-source and peer-reviewable. Full code, datasets and validation at:"}
          </p>
          <a
            href="https://pypi.org/project/nutramilo-nis/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nl-pypi-link"
            className={`inline-flex items-center gap-1.5 mt-2 text-sm font-bold hover:underline ${
              isDark ? "text-sky-300" : "text-sky-700"
            }`}
          >
            <span className="font-mono">pypi.org/project/nutramilo-nis</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────
// HistoryCard — one thumbnail in the horizontal carousel
// Clickable to load the item back into the main result panel (so users
// can edit + recalculate macros for any past scan of the day).
// ─────────────────────────────────────────────────────────────────────
function HistoryCard({ item, isDark, bg, onRemove, onSelect }) {
  const tier = item.nis_tier?.toLowerCase() || "";
  const tierClr = tier === "high" ? "bg-red-500" : tier === "medium" || tier === "med" ? "bg-amber-500" : tier === "low" ? "bg-emerald-500" : "bg-slate-500";
  return (
    <div
      className={`snap-start flex-shrink-0 w-40 rounded-xl border overflow-hidden relative group ${
        isDark ? "border-white/10 bg-white/5 hover:border-sky-400/50" : "border-slate-200 bg-white shadow hover:border-sky-400"
      } transition-colors cursor-pointer`}
      data-testid="nl-history-item"
      onClick={() => onSelect?.(item)}
      role="button"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        aria-label="remove"
        className="absolute top-1 right-1 z-10 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
      >
        <XIcon className="w-3 h-3" />
      </button>
      {item.thumb ? (
        <img src={item.thumb} alt={item.food_name} className="w-full h-20 object-cover" />
      ) : (
        <div className={`w-full h-20 flex items-center justify-center ${isDark ? "bg-slate-800" : "bg-slate-100"}`}>
          <ScanLine className={`w-6 h-6 ${isDark ? "text-slate-500" : "text-slate-400"}`} />
        </div>
      )}
      <div className="p-2">
        <div className={`text-[11px] font-bold truncate ${isDark ? "text-slate-100" : "text-slate-900"}`}>
          {item.food_name || (bg ? "Продукт" : "Item")}
        </div>
        <div className="flex items-center gap-1 mt-1">
          <span className={`${tierClr} text-white text-[9px] font-black px-1.5 py-0.5 rounded`}>
            NIS {item.nis_percent != null ? Math.round(item.nis_percent) : "—"}%
          </span>
          <span className={`text-[9px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            DIL {item.dil_g != null ? item.dil_g.toFixed(0) : "—"}g
          </span>
        </div>
        <div className={`text-[9px] mt-1 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ${
          isDark ? "text-sky-300" : "text-sky-700"
        }`}>
          <Pencil className="w-2.5 h-2.5" />
          {bg ? "Редакция" : "Edit"}
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// ResultCard — macros + DIL/NIS badges + educational note
// When `editable` is true, users can tap the pencil to open inline edit
// mode, correct AI-misread macros, and recalculate DIL/NIS without a re-scan.
// ─────────────────────────────────────────────────────────────────────
function ResultCard({ result, bg, isDark, isCombined = false, editable = false, recalculating = false, onRecalculate }) {
  const isLabel = result.is_nutrition_label;
  const dilTier = result.dil_g <= 30 ? "low" : result.dil_g <= 60 ? "med" : "high";
  const nisPct = result.nis_percent;
  const nisTier = (result.nis_tier || "").toLowerCase() || (nisPct == null ? "n/a" : nisPct < 30 ? "low" : nisPct < 60 ? "med" : "high");

  const tierColor = (t) => {
    if (t === "low")  return "bg-emerald-500";
    if (t === "med" || t === "medium")  return "bg-amber-500";
    if (t === "high") return "bg-red-500";
    return "bg-slate-500";
  };

  // ─── Edit mode ─────────────────────────────────────────────────────
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(null);

  const openEdit = () => {
    setDraft({
      food_name: result.food_name || "",
      calories: result.calories ?? 0,
      protein_g: result.protein_g ?? 0,
      carbs_g: result.carbs_g ?? 0,
      fat_g: result.fat_g ?? 0,
      fiber_g: result.fiber_g ?? 0,
      sugar_g: result.sugar_g ?? null,
      saturated_fat_g: result.saturated_fat_g ?? null,
      sodium_mg: result.sodium_mg ?? null,
      portion_g: result.portion_g ?? null,
    });
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setDraft(null); };
  const saveEdit = async () => {
    if (!draft || !onRecalculate) return;
    // Coerce empty strings back to null for optional macros.
    const clean = {
      ...draft,
      calories: Number(draft.calories) || 0,
      protein_g: Number(draft.protein_g) || 0,
      carbs_g: Number(draft.carbs_g) || 0,
      fat_g: Number(draft.fat_g) || 0,
      fiber_g: Number(draft.fiber_g) || 0,
      sugar_g: draft.sugar_g === null || draft.sugar_g === "" ? null : Number(draft.sugar_g),
      saturated_fat_g: draft.saturated_fat_g === null || draft.saturated_fat_g === "" ? null : Number(draft.saturated_fat_g),
      sodium_mg: draft.sodium_mg === null || draft.sodium_mg === "" ? null : Number(draft.sodium_mg),
      portion_g: draft.portion_g === null || draft.portion_g === "" ? null : Number(draft.portion_g),
    };
    await onRecalculate(clean);
    setEditing(false);
    setDraft(null);
  };
  const patch = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <>
      {/* Header — detected food + type badge + edit toggle */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          {editing ? (
            <input
              type="text"
              value={draft.food_name}
              onChange={(e) => patch("food_name", e.target.value)}
              data-testid="nl-edit-food-name"
              className={`w-full text-base font-bold px-2 py-1 rounded border ${
                isDark ? "bg-slate-800 border-white/20 text-white" : "bg-white border-slate-300 text-slate-900"
              }`}
            />
          ) : (
            <h3 className={`text-base font-bold truncate ${isDark ? "text-white" : "text-slate-900"}`}
                data-testid={isCombined ? "nl-combined-name" : "nl-result-name"}>
              {result.food_name}
            </h3>
          )}
          <div className={`text-[10px] font-semibold uppercase tracking-wider mt-0.5 ${
            isCombined ? "text-purple-500" : isLabel ? "text-purple-500" : "text-sky-500"
          }`}>
            {isCombined
              ? (bg ? `Сумарно · ${result.item_count} елемента` : `Total · ${result.item_count} items`)
              : isLabel
              ? (bg ? "Разчетен етикет · per 100g" : "Nutrition label · per 100g")
              : (bg ? `Ястие · ${Math.round(result.portion_g || 0)}g` : `Dish · ${Math.round(result.portion_g || 0)}g`)}
            {!isCombined && (
              <span className={`ml-2 ${isDark ? "text-slate-400" : "text-slate-500"} normal-case`}>
                conf {Math.round((result.confidence || 0) * 100)}%
              </span>
            )}
          </div>
        </div>
        {editable && !editing && (
          <button
            onClick={openEdit}
            data-testid="nl-edit-btn"
            title={bg ? "Редактирай стойностите" : "Edit values"}
            className={`flex-shrink-0 h-8 px-2 rounded-lg border text-[11px] font-bold flex items-center gap-1 transition ${
              isDark ? "border-white/20 text-slate-200 hover:bg-white/10" : "border-slate-300 text-slate-700 hover:bg-slate-100"
            }`}
          >
            <Pencil className="w-3 h-3" />
            {bg ? "Редакция" : "Edit"}
          </button>
        )}
      </div>

      {/* Warning when the AI confidence is low — nudge user to verify */}
      {editable && !editing && (result.confidence ?? 1) < 0.75 && (
        <div className={`mb-3 p-2 rounded-lg border text-[11px] flex items-start gap-1.5 ${
          isDark ? "bg-amber-500/15 border-amber-500/40 text-amber-100" : "bg-amber-50 border-amber-300 text-amber-900"
        }`}>
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>
            {bg
              ? `Ниска увереност на AI — проверете и коригирайте стойностите с бутон «Редакция».`
              : `Low AI confidence — verify and correct the values via the "Edit" button.`}
          </span>
        </div>
      )}

      {/* DIL + NIS score badges */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <ScoreBadge label="DIL"
          value={result.dil_g}
          unit="g"
          tierColor={tierColor(dilTier)}
          hint={bg ? "инсулинов товар" : "insulin load"}
        />
        <ScoreBadge label="NIS"
          value={nisPct}
          unit="%"
          tierColor={tierColor(nisTier)}
          hint={bg ? "инсулинов индекс" : "insulin score"}
        />
      </div>

      {/* Macros table (or edit form) */}
      <div className={`p-3 rounded-lg border text-xs ${isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"}`}
           data-testid={isCombined ? "nl-combined-macros" : "nl-macros"}>
        {editing ? (
          <EditForm draft={draft} patch={patch} bg={bg} isDark={isDark} />
        ) : (
          <div className="grid grid-cols-2 gap-y-1.5">
            <MacroRow label={bg ? "Калории" : "Calories"} value={`${Math.round(result.calories || 0)} kcal`} isDark={isDark} />
            <MacroRow label={bg ? "Белтъчини" : "Protein"} value={`${(result.protein_g || 0).toFixed(1)} g`} isDark={isDark} />
            <MacroRow label={bg ? "Въглехидрати" : "Carbs"} value={`${(result.carbs_g || 0).toFixed(1)} g`} isDark={isDark} />
            <MacroRow label={bg ? "Мазнини" : "Fat"} value={`${(result.fat_g || 0).toFixed(1)} g`} isDark={isDark} />
            <MacroRow label={bg ? "Фибри" : "Fiber"} value={`${(result.fiber_g || 0).toFixed(1)} g`} isDark={isDark} />
            {result.sugar_g != null && (
              <MacroRow label={bg ? "Захари" : "Sugar"} value={`${result.sugar_g.toFixed(1)} g`} isDark={isDark} />
            )}
            {result.saturated_fat_g != null && (
              <MacroRow label={bg ? "Нас. мазнини" : "Sat. fat"} value={`${result.saturated_fat_g.toFixed(1)} g`} isDark={isDark} />
            )}
            {result.sodium_mg != null && (
              <MacroRow label={bg ? "Натрий" : "Sodium"} value={`${result.sodium_mg.toFixed(0)} mg`} isDark={isDark} />
            )}
          </div>
        )}
      </div>

      {/* Edit action buttons */}
      {editing && (
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={saveEdit}
            disabled={recalculating}
            data-testid="nl-recalculate-btn"
            className={`flex-1 h-9 rounded-lg font-bold text-sm text-white shadow gradient-primary hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-1.5`}
          >
            {recalculating
              ? <><Loader2 className="w-4 h-4 animate-spin" />{bg ? "Преизчислявам…" : "Recalculating…"}</>
              : <><CheckIcon className="w-4 h-4" />{bg ? "Преизчисли DIL / NIS" : "Recalculate DIL / NIS"}</>}
          </button>
          <button
            onClick={cancelEdit}
            disabled={recalculating}
            data-testid="nl-edit-cancel-btn"
            className={`h-9 px-3 rounded-lg border text-sm font-semibold ${
              isDark ? "border-white/20 text-slate-200 hover:bg-white/10" : "border-slate-300 text-slate-700 hover:bg-slate-100"
            }`}
          >
            {bg ? "Отказ" : "Cancel"}
          </button>
        </div>
      )}

      {/* Educational note */}
      {!editing && result.educational_note && (
        <div className={`mt-3 p-2.5 rounded-lg border text-[11px] flex items-start gap-1.5 ${
          isDark ? "bg-sky-500/10 border-sky-500/30 text-sky-100" : "bg-sky-50 border-sky-200 text-sky-800"
        }`}>
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>{result.educational_note}</span>
        </div>
      )}

      {/* Disclaimer */}
      <p className={`text-[9px] italic mt-2 ${isDark ? "text-slate-500" : "text-slate-400"}`}>
        {bg ? result.nutramilo_disclaimer_bg : result.nutramilo_disclaimer_en}
      </p>
    </>
  );
}


// ─── Inline edit form (macros only, no scores) ─────────────────────────
function EditForm({ draft, patch, bg, isDark }) {
  const fields = [
    { k: "calories",         labelBg: "Калории",     labelEn: "Calories",  unit: "kcal", step: 1,    required: true },
    { k: "protein_g",        labelBg: "Белтъчини",   labelEn: "Protein",   unit: "g",    step: 0.1,  required: true },
    { k: "carbs_g",          labelBg: "Въглехидрати",labelEn: "Carbs",     unit: "g",    step: 0.1,  required: true },
    { k: "fat_g",            labelBg: "Мазнини",     labelEn: "Fat",       unit: "g",    step: 0.1,  required: true },
    { k: "fiber_g",          labelBg: "Фибри",       labelEn: "Fiber",     unit: "g",    step: 0.1,  required: true },
    { k: "sugar_g",          labelBg: "Захари",      labelEn: "Sugar",     unit: "g",    step: 0.1 },
    { k: "saturated_fat_g",  labelBg: "Нас. мазн.",  labelEn: "Sat. fat",  unit: "g",    step: 0.1 },
    { k: "sodium_mg",        labelBg: "Натрий",      labelEn: "Sodium",    unit: "mg",   step: 1 },
    { k: "portion_g",        labelBg: "Порция",      labelEn: "Portion",   unit: "g",    step: 1 },
  ];
  return (
    <div className="space-y-2" data-testid="nl-edit-form">
      <p className={`text-[10px] italic ${isDark ? "text-slate-400" : "text-slate-500"}`}>
        {bg
          ? `Коригирайте стойностите и натиснете «Преизчисли». Формулите DIL v2.0 и NIS v1.1.9 се преизчисляват без нов AI разход.`
          : `Correct any values and press "Recalculate". DIL v2.0 and NIS v1.1.9 formulas run instantly with no new AI cost.`}
      </p>
      {fields.map((f) => (
        <div key={f.k} className="grid grid-cols-[1fr_auto_auto] items-center gap-2">
          <label className={`text-[11px] font-semibold ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            {bg ? f.labelBg : f.labelEn}
          </label>
          <input
            type="number"
            step={f.step}
            min="0"
            value={draft[f.k] == null ? "" : draft[f.k]}
            onChange={(e) => patch(f.k, e.target.value === "" ? null : e.target.value)}
            placeholder={f.required ? "0" : "—"}
            data-testid={`nl-edit-${f.k}`}
            className={`w-24 text-right px-2 py-1 rounded border text-xs font-bold ${
              isDark ? "bg-slate-800 border-white/20 text-white" : "bg-white border-slate-300 text-slate-900"
            }`}
          />
          <span className={`text-[10px] w-8 ${isDark ? "text-slate-500" : "text-slate-400"}`}>{f.unit}</span>
        </div>
      ))}
    </div>
  );
}



function ScoreBadge({ label, value, unit, tierColor, hint }) {
  const displayVal = value == null ? "—" : (unit === "%" ? Math.round(value) : value.toFixed(1));
  return (
    <div className={`${tierColor} rounded-xl p-3 text-white flex flex-col items-center justify-center`}
         data-testid={`nl-badge-${label.toLowerCase()}`}>
      <div className="text-[9px] font-bold uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-2xl font-black leading-none">{displayVal}{value != null && <span className="text-xs">{unit}</span>}</div>
      <div className="text-[9px] opacity-80">{hint}</div>
    </div>
  );
}


function MacroRow({ label, value, isDark }) {
  return (
    <>
      <div className={isDark ? "text-slate-400" : "text-slate-500"}>{label}</div>
      <div className={`text-right font-bold ${isDark ? "text-slate-100" : "text-slate-900"}`}>{value}</div>
    </>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Col3PreviewCard — iter732 (Feb 2026)
// A landing-only "COL3 synthesis" preview computed from macros ALONE.
// Nutrition Facts labels never list Vit C / Zn / Cu / sulfur / silica /
// glycine / proline — so we honestly show what CAN be inferred from macros
// (amino-acid supply + AGE / oxidative load) and mark the 5+ true cofactors
// as "not measurable from label → registration required".
//
// Formula (0-100, transparent):
//   base 50
//   + protein  × 0.6  (cap +25)   — amino acid supply → glycine/proline proxy
//   + fiber    × 0.5  (cap +10)   — antioxidant environment proxy
//   - sugar    × 0.4  (cap -15)   — AGE crosslink driver (Nature Aging 2019)
//   - sat-fat  × 0.3  (cap -10)   — oxidative burden
//   - max(0, (Na-1500)) × 0.005   — sodium → LOX↑ (Circ Res 2021)
//   clamp [0, 100]
// ─────────────────────────────────────────────────────────────────────
function Col3PreviewCard({ result, bg, isDark }) {
  const p  = result.protein_g || 0;
  const fi = result.fiber_g   || 0;
  const su = result.sugar_g   || 0;
  const sf = result.saturated_fat_g || 0;
  const na = result.sodium_mg || 0;

  const proteinBonus = Math.min(p * 0.6, 25);
  const fiberBonus   = Math.min(fi * 0.5, 10);
  const sugarPenalty = Math.min(su * 0.4, 15);
  const satPenalty   = Math.min(sf * 0.3, 10);
  const naPenalty    = Math.max(0, (na - 1500) * 0.005);

  const rawScore = 50 + proteinBonus + fiberBonus - sugarPenalty - satPenalty - naPenalty;
  const score = Math.round(Math.max(0, Math.min(100, rawScore)));

  const tier = score >= 70 ? "high" : score >= 50 ? "medium" : score >= 30 ? "low" : "verylow";
  const tierBg = {
    high:    "bg-emerald-500",
    medium:  "bg-amber-500",
    low:     "bg-orange-500",
    verylow: "bg-red-500",
  }[tier];
  const tierLbl = bg
    ? { high: "ВИСОК", medium: "СРЕДЕН", low: "НИСЪК", verylow: "МНОГО НИСЪК" }[tier]
    : { high: "HIGH",  medium: "MEDIUM", low: "LOW",    verylow: "VERY LOW"    }[tier];

  // AGE-antagonist detection — the ONE inference we can make from macros
  const ageBurden = su >= 15 || sf >= 8 || na >= 1500;

  // Cofactor coverage (mostly "not measurable from label")
  const cofactors = [
    { key: "aa", labelBg: "Аминокиселини (глицин/пролин)", labelEn: "Amino acids (glycine/proline)",
      status: p >= 20 ? "ok" : p >= 10 ? "partial" : "low",
      detailBg: p >= 20 ? `${p.toFixed(0)} g белтъци — добър запас` : p >= 10 ? `${p.toFixed(0)} g — умерено` : `${p.toFixed(0)} g — недостатъчно`,
      detailEn: p >= 20 ? `${p.toFixed(0)} g protein — good supply` : p >= 10 ? `${p.toFixed(0)} g — moderate` : `${p.toFixed(0)} g — low`,
    },
    { key: "ages", labelBg: "AGE-антагонисти (захар + нас. мазн. + Na)", labelEn: "AGE burden (sugar + sat fat + Na)",
      status: ageBurden ? "bad" : "ok",
      detailBg: ageBurden ? `Висок AGE-товар → блокира колаген crosslinks` : `Нисък AGE-товар — благоприятно`,
      detailEn: ageBurden ? `High AGE burden → blocks collagen crosslinks` : `Low AGE burden — favourable`,
    },
    // The rest cannot be measured from a nutrition facts label
    { key: "vitc",   labelBg: "Витамин C",         labelEn: "Vitamin C",   status: "unknown", detailBg: "не се измерва от етикет", detailEn: "not on label" },
    { key: "zinc",   labelBg: "Цинк",              labelEn: "Zinc",        status: "unknown", detailBg: "не се измерва от етикет", detailEn: "not on label" },
    { key: "copper", labelBg: "Мед",               labelEn: "Copper",      status: "unknown", detailBg: "не се измерва от етикет", detailEn: "not on label" },
    { key: "sulf",   labelBg: "Сяра (алицин / SMA)", labelEn: "Sulfur",     status: "unknown", detailBg: "не се измерва от етикет", detailEn: "not on label" },
    { key: "silica", labelBg: "Силициев диоксид",  labelEn: "Silica",       status: "unknown", detailBg: "не се измерва от етикет", detailEn: "not on label" },
  ];

  const unknownCount = cofactors.filter((c) => c.status === "unknown").length;

  const statusPill = (s) => {
    if (s === "ok")      return isDark ? "bg-emerald-500/20 text-emerald-200 border-emerald-500/40" : "bg-emerald-100 text-emerald-800 border-emerald-300";
    if (s === "partial") return isDark ? "bg-amber-500/20   text-amber-200   border-amber-500/40"   : "bg-amber-100   text-amber-800   border-amber-300";
    if (s === "low")     return isDark ? "bg-orange-500/20  text-orange-200  border-orange-500/40"  : "bg-orange-100  text-orange-800  border-orange-300";
    if (s === "bad")     return isDark ? "bg-red-500/20     text-red-200     border-red-500/40"     : "bg-red-100     text-red-800     border-red-300";
    return isDark ? "bg-slate-700/50 text-slate-300 border-slate-600" : "bg-slate-100 text-slate-600 border-slate-300";
  };
  const statusIcon = (s) => (s === "ok" ? "✓" : s === "partial" ? "~" : s === "low" ? "!" : s === "bad" ? "✕" : "?");

  return (
    <div
      data-testid="nl-col3-preview"
      className={`mt-5 p-5 rounded-2xl border ${
        isDark ? "border-white/10 bg-gradient-to-br from-emerald-950/30 via-slate-900 to-teal-950/30"
               : "border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50"
      }`}
    >
      {/* Header + score badge */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className={`text-[10px] font-bold uppercase tracking-widest ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
            {bg ? "COL3 · Синтез на колаген тип III" : "COL3 · Type-3 collagen synthesis"}
          </div>
          <div className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            {bg ? "Прогноза от макросите" : "Macro-only estimate"}
          </div>
        </div>
        <div className={`${tierBg} text-white rounded-xl px-3 py-2 flex flex-col items-center min-w-[80px]`}
             data-testid="nl-col3-score">
          <div className="text-2xl font-black leading-none">{score}</div>
          <div className="text-[9px] font-bold opacity-90 mt-0.5">{tierLbl}</div>
        </div>
      </div>

      {/* Formula transparency (small) */}
      <div className={`text-[10px] mb-3 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
        {bg
          ? `Проксимална оценка: белтък +${proteinBonus.toFixed(0)} · фибри +${fiberBonus.toFixed(0)} · захар −${sugarPenalty.toFixed(0)} · нас.мазн. −${satPenalty.toFixed(0)} · Na −${naPenalty.toFixed(0)}`
          : `Proxy score: protein +${proteinBonus.toFixed(0)} · fiber +${fiberBonus.toFixed(0)} · sugar −${sugarPenalty.toFixed(0)} · sat.fat −${satPenalty.toFixed(0)} · Na −${naPenalty.toFixed(0)}`}
      </div>

      {/* Cofactor grid */}
      <div className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
        {bg ? "COL3 кофактори — покритие" : "COL3 cofactors — coverage"}
      </div>
      <div className="grid sm:grid-cols-2 gap-1.5" data-testid="nl-col3-cofactors">
        {cofactors.map((c) => (
          <div
            key={c.key}
            className={`flex items-center gap-2 p-2 rounded-lg border text-[11px] ${statusPill(c.status)}`}
          >
            <span className="w-5 h-5 rounded-full flex items-center justify-center font-black text-[11px] bg-white/40">
              {statusIcon(c.status)}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{bg ? c.labelBg : c.labelEn}</div>
              <div className="opacity-80 truncate">{bg ? c.detailBg : c.detailEn}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Registration nudge — this is the real conversion driver */}
      <div className={`mt-4 p-3 rounded-lg border-l-4 ${
        isDark ? "bg-emerald-500/10 border-emerald-400 text-emerald-100" : "bg-emerald-50 border-emerald-500 text-emerald-900"
      }`}>
        <div className="flex items-start gap-2">
          <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-xs font-bold">
              {bg
                ? `${unknownCount} от 7 кофактора не могат да бъдат прочетени от етикет`
                : `${unknownCount} of 7 cofactors are not readable from a nutrition label`}
            </div>
            <div className={`text-[11px] mt-0.5 ${isDark ? "text-emerald-200/80" : "text-emerald-800/80"}`}>
              {bg
                ? "За пълен COL3 анализ (Vit C, Zn, Cu, сяра, силиций, глицин, пролин) е нужна нашата курирана микронутриентна база."
                : "For full COL3 analysis (Vit C, Zn, Cu, sulfur, silica, glycine, proline) our curated micronutrient DB is required."}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

